import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CodeBracketIcon,
  MagnifyingGlassIcon,
  CogIcon,
  ServerIcon,
  CircleStackIcon,
  WrenchScrewdriverIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import AnimatedBackground from '../components/AnimatedBackground';

const AllServices = () => {
  const services = [
    {
      icon: CodeBracketIcon,
      title: 'Web Development',
      description: 'Custom websites and web applications built with modern technologies. From responsive design to complex web systems, we deliver solutions that perform.',
      features: ['Responsive Design', 'Modern Frameworks', 'Performance Optimization'],
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/web-development',
    },
    {
      icon: MagnifyingGlassIcon,
      title: 'SEO Optimization',
      description: 'Boost your online visibility and drive organic traffic with our comprehensive SEO strategies and digital marketing solutions.',
      features: ['Keyword Research', 'On-Page Optimization', 'Performance Tracking'],
      gradient: 'from-green-400 to-green-500',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/seo-optimization',
    },
    {
      icon: CogIcon,
      title: 'IT Consulting',
      description: 'Strategic IT guidance to help your business leverage technology effectively and make informed decisions about your digital infrastructure.',
      features: ['System Analysis', 'Technology Planning', 'Process Optimization'],
      gradient: 'from-green-600 to-green-700',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/it-consulting',
    },
    {
      icon: ServerIcon,
      title: 'System Management',
      description: 'Complete management of computer systems and IT infrastructure to ensure optimal performance and security for your business operations.',
      features: ['Server Management', 'Security Monitoring', 'Backup Solutions'],
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/system-management',
    },
    {
      icon: CircleStackIcon,
      title: 'Data Processing',
      description: 'Advanced data processing and web hosting solutions to manage your business information efficiently and securely.',
      features: ['Data Analytics', 'Cloud Hosting', 'Database Optimization'],
      gradient: 'from-green-400 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/data-processing',
    },
    {
      icon: WrenchScrewdriverIcon,
      title: 'Custom Software',
      description: 'Bespoke software solutions and applications designed specifically for your business requirements and workflows.',
      features: ['Custom Applications', 'Software Integration', 'Maintenance Support'],
      gradient: 'from-green-600 to-green-500',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/custom-software',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-900 to-green-800 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/"
              className="inline-flex items-center text-green-300 hover:text-green-200 mb-8 transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
            
            <div className="text-center">
              <h1 className="text-5xl font-bold text-white mb-6">
                Our Services
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Comprehensive IT solutions tailored to your business needs and designed to drive growth in the digital world.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="py-20 relative overflow-hidden">
        {/* Animated Background */}
        <AnimatedBackground />
        
        {/* Gradient Orbs */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-green-600/10 to-green-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="h-full flex flex-col"
                >
                  <Link to={service.link} className="h-full flex flex-col group">
                    <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-green-500 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl h-full flex flex-col">
                      <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      
                      <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-green-400 transition-colors duration-300">
                        {service.title}
                      </h3>
                      
                      <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
                        {service.description}
                      </p>
                      
                      <div className="space-y-3 mb-6">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                            <span className="text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-auto">
                        <div className="inline-flex items-center text-green-400 font-medium group-hover:text-green-300 transition-colors duration-300">
                          Learn More
                          <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="text-center mt-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-3xl p-12 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-white/10" style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: '30px 30px'
              }}></div>
              
              <div className="relative z-10">
                <h3 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
                  Ready to Transform Your Business?
                </h3>
                <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                  Let's discuss how our comprehensive IT solutions can help you achieve your goals and drive growth.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AllServices;
